import {
  Competency_Map_Status,
  CompetencyMapDocument,
  CompetencyMapQuery,
  CompetencyMapQuestionsDocument,
  useDeleteCompetencyMapMutation,
  useDeleteNotConfirmedQuestionsMutation,
} from "@generated/graphql";
import { MoreHorizOutlined } from "@mui/icons-material";
import { Button, IconButton, Popover, Stack, Tooltip } from "@mui/material";
import ROUTES from "constants/routes";
import typography from "constants/typography.constants";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import useDownloadCompetencyMap from "hooks/useDownloadCompetencyMap";
import useUpdateCompetencyMapStatus from "hooks/useUpdateCompetencyMapStatus";
import { MouseEvent, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { palette } from "theme";
import { ActionButton } from "./types";

type MoreActionsButtonProps = {
  competencyMap: CompetencyMapQuery["competencyMap"];
  isCreate: boolean;
  onOpenCopyModal: () => void;
  onOpenGenerateQuestionsForCMModal: () => void;
};

const MoreActionsButton = ({
  competencyMap,
  isCreate,
  onOpenCopyModal,
  onOpenGenerateQuestionsForCMModal,
}: MoreActionsButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { mapId } = useParams();
  const { addToast } = useContext(ToastContext);
  const { handleOpen: handleOpenConfirmModal } = useContext(ConfirmModalContext);
  const [deleteMap] = useDeleteCompetencyMapMutation();
  const [deleteNotConfirmedQuestions] = useDeleteNotConfirmedQuestionsMutation();
  const { getCompetencyMapFile } = useDownloadCompetencyMap();
  const { handleArchiveCompetencyMap, handleUpdateCompetencyMapStatus } = useUpdateCompetencyMapStatus({
    competencyMap,
  });

  const handlePopoverOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteMap = async () => {
    const competencyMapId = mapId ?? competencyMap?.id;

    if (!competencyMapId) return;

    await deleteMap({
      variables: {
        competencyMapId,
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: typography.competencyMap.deleted });
        navigate(`/${ROUTES.COMPETENCE_MAPS}`);
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const handleOpenConfirmDeleteMapModal = () => {
    handleOpenConfirmModal({
      title: "Вы уверены, что хотите удалить карту компетенций?",
      actionButtonText: "Удалить",
      handleClick: handleDeleteMap,
    });
  };

  const handleOpenConfirmRecoverModal = () => {
    handleOpenConfirmModal({
      title: typography.competencyMap.recover.title,
      actionButtonText: typography.competencyMap.recover.confirm,
      handleClick: () => handleUpdateCompetencyMapStatus(Competency_Map_Status.Draft),
    });
  };

  const handleDeleteNotConfirmedQuestions = () => {
    const competencyMapId = mapId ?? competencyMap?.id;

    if (!competencyMapId) return;

    deleteNotConfirmedQuestions({
      variables: {
        competencyMapId,
      },
      refetchQueries: [
        {
          query: CompetencyMapDocument,
          variables: { competencyMapId },
        },
        {
          query: CompetencyMapQuestionsDocument,
          variables: { competencyMapId },
        },
      ],
      awaitRefetchQueries: true,
    })
      .then(() => {
        addToast({
          type: ToastTypeEnum.SUCCESS,
          text: "Неподтвержденные вопросы удалены",
        });
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const handleOpenConfirmDeleteNotConfirmedQuestionsModal = () => {
    handleOpenConfirmModal({
      title: "Вы уверены, что хотите удалить неподтвержденные вопросы?",
      actionButtonText: "Удалить",
      handleClick: handleDeleteNotConfirmedQuestions,
    });
  };

  const actions: ActionButton[] = [
    {
      id: "generate",
      tooltip: "Генерация вопросов на всю карту компетенций",
      title: "Сгенерировать вопросы",
      onClick: onOpenGenerateQuestionsForCMModal,
      disabled: isCreate,
    },
    {
      id: "duplicate",
      tooltip: "Создание копии полной карты этой версии",
      title: "Дублировать карту",
      onClick: onOpenCopyModal,
      disabled: isCreate,
    },
    {
      id: "download",
      title: "Выгрузить карту (xlsx)",
      onClick: () => getCompetencyMapFile(competencyMap),
      disabled: isCreate,
    },
    competencyMap?.status === Competency_Map_Status.Archive
      ? {
          id: "recover",
          title: typography.competencyMap.recover.confirm,
          onClick: handleOpenConfirmRecoverModal,
          disabled: isCreate,
        }
      : {
          id: "archive",
          title: typography.competencyMap.archive.buttonTitle,
          onClick: handleArchiveCompetencyMap,
          disabled: isCreate,
        },
    {
      id: "delete-not-confirmed-questions",
      title: "Удалить неподтвержденные вопросы",
      onClick: handleOpenConfirmDeleteNotConfirmedQuestionsModal,
      disabled: isCreate,
    },
    {
      id: "delete",
      title: "Удалить",
      onClick: handleOpenConfirmDeleteMapModal,
      disabled: isCreate,
    },
  ];

  return (
    <>
      <IconButton
        size='small'
        onClick={handlePopoverOpen}
        disabled={isCreate}
        sx={{
          backgroundColor: palette.blue[100],
          color: palette.white,
          ":hover": { backgroundColor: palette.blue[100], opacity: 0.9 },
        }}>
        <MoreHorizOutlined />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus>
        <Stack sx={{ py: 0.5 }}>
          {actions.map(({ id, title, tooltip, onClick, ...buttonProps }) => (
            <Tooltip
              key={id}
              title={tooltip || ""}>
              <span style={{ display: "block" }}>
                <Button
                  variant='text'
                  fullWidth
                  sx={{ justifyContent: "flex-start", px: 2.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePopoverClose();
                    onClick();
                  }}
                  {...buttonProps}>
                  {title}
                </Button>
              </span>
            </Tooltip>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default MoreActionsButton;
